import { API, graphqlOperation } from 'aws-amplify'
import { useEffect, useState } from 'react'

const op = `query ListTeamMembers($pk: ID, $d1: String) {
    listTeamMembers(pk: $pk, d1: $d1) {
        items {
            pk
            sk
            d1
            d2
            firstName
            lastName
            email
            tenant
            positionId
            floor
            suite
            phone {
                number
                extension
            }
            workPhone {
                number
                extension
            }
            assignedOn
            assignedBy
            acceptedOn
            modifiedAt
        }
    }
}`

const cache = {}

// Members added this session (self-join, assignment) that server responses
// must not lose: listTeamMembers is eventually consistent, so a refetch right
// after the create (e.g. navigating back to the Teams landing page, which
// decides the Team Member medal from this cache) can re-serve a list missing
// the new record. Merged back into every stored response until the server
// catches up or the TTL passes.
const RECENT_ADDITION_TTL_MS = 5 * 60 * 1000
const recentAdditions = new Map() // `${teamPk}|${sk}` -> { member, ts }
// Mirror guard for deletes (decline, leave team): a stale refetch must not
// resurrect a record the user just removed.
const recentRemovals = new Map() // `${teamPk}|${sk}` -> ts

const mergeRecentAdditions = (teamPk, entry) => {
    const items = entry?.items
    if (!Array.isArray(items)) return entry
    const now = Date.now()

    let merged = items
    recentRemovals.forEach((ts, key) => {
        if (now - ts > RECENT_ADDITION_TTL_MS) {
            recentRemovals.delete(key)
            return
        }
        if (!key.startsWith(`${teamPk}|`)) return
        const sk = key.slice(`${teamPk}|`.length)
        merged = merged.filter((m) => m.sk !== sk)
    })

    const missing = []
    recentAdditions.forEach(({ member, ts }, key) => {
        if (now - ts > RECENT_ADDITION_TTL_MS) {
            recentAdditions.delete(key)
            return
        }
        if (!key.startsWith(`${teamPk}|`)) return
        if (merged.some((m) => m.sk === member.sk)) {
            // The server now returns the record — the safety net is done.
            recentAdditions.delete(key)
            return
        }
        missing.push(member)
    })
    if (missing.length) merged = [...merged, ...missing]

    return merged === items ? entry : { ...entry, items: merged }
}

const subscribers = new Map()

const subscribe = (cacheKey, callback) => {
    if (!subscribers.has(cacheKey)) subscribers.set(cacheKey, new Set())
    subscribers.get(cacheKey).add(callback)
    return () => {
        const forKey = subscribers.get(cacheKey)
        if (!forKey) return
        forKey.delete(callback)
        if (forKey.size === 0) subscribers.delete(cacheKey)
    }
}

const notify = (cacheKey) => {
    const forKey = subscribers.get(cacheKey)
    if (!forKey) return
    forKey.forEach((callback) => callback(cache[cacheKey]?.items || []))
}

const storeMembers = (cacheKey, entry) => {
    cache[cacheKey] = mergeRecentAdditions(cacheKey, entry)
}

const runQuery = async ({ pk, d1 }) => {
    const response = await API.graphql(graphqlOperation(op, { pk, d1, sk: 'TEAM_MEMBER' }))
    return response.data.listTeamMembers
}


export const applyPositionChangesToTeamMembers = (teamPk, changes) => {
    if (!teamPk || !changes) return

    const { renamed = [], deleted = [] } = changes
    if (renamed.length === 0 && deleted.length === 0) return

    const renameMap = new Map(renamed.map((r) => [r.from, r.to]))
    const deletedTitles = new Set(deleted.map((d) => d.title).filter(Boolean))

    Object.keys(cache).forEach((cacheKey) => {
        const entry = cache[cacheKey]
        const items = entry?.items
        if (!Array.isArray(items)) return

        let touched = false
        const nextItems = []

        items.forEach((member) => {
            if (member.pk !== teamPk) {
                nextItems.push(member)
                return
            }
            if (deletedTitles.has(member.position)) {
                touched = true
                return
            }
            if (renameMap.has(member.position)) {
                touched = true
                nextItems.push({ ...member, position: renameMap.get(member.position) })
                return
            }
            nextItems.push(member)
        })

        if (!touched) return
        cache[cacheKey] = { ...entry, items: nextItems }
        notify(cacheKey)
    })
}


// Insert one member record into the cache (self-join, assignment) and notify
// subscribers, so surfaces reading this cache (e.g. the Teams landing page's
// Team Member medal) update without a refresh. Also remembered as a recent
// addition so an eventually-consistent refetch can't drop it again.
export const addTeamMemberToCache = (teamPk, member) => {
    if (!teamPk || !member?.sk) return
    const record = { ...member, pk: teamPk }
    // A rejoin reuses the same sk — an old removal guard must not strip it.
    recentRemovals.delete(`${teamPk}|${record.sk}`)
    recentAdditions.set(`${teamPk}|${record.sk}`, { member: record, ts: Date.now() })
    const entry = cache[teamPk]
    if (!entry?.items) {
        cache[teamPk] = { items: [record] }
    } else if (!entry.items.some((m) => m.sk === record.sk)) {
        cache[teamPk] = { ...entry, items: [...entry.items, record] }
    }
    notify(teamPk)
}

// Patch one member record in the cache (e.g. stamping acceptedOn after the
// user accepts an assignment) and notify subscribers.
export const updateTeamMemberInCache = (teamPk, sk, patch) => {
    const recent = recentAdditions.get(`${teamPk}|${sk}`)
    if (recent) {
        recentAdditions.set(`${teamPk}|${sk}`, { ...recent, member: { ...recent.member, ...patch } })
    }
    const entry = cache[teamPk]
    if (!entry?.items) return
    cache[teamPk] = {
        ...entry,
        items: entry.items.map((m) => (m.sk === sk ? { ...m, ...patch } : m)),
    }
    notify(teamPk)
}

// Drop one member record from the cache (e.g. after the user declines an
// assignment or leaves the team and the record is deleted server-side) and
// notify subscribers.
export const removeTeamMemberFromCache = (teamPk, sk) => {
    recentAdditions.delete(`${teamPk}|${sk}`)
    recentRemovals.set(`${teamPk}|${sk}`, Date.now())
    const entry = cache[teamPk]
    if (!entry?.items) return
    cache[teamPk] = {
        ...entry,
        items: entry.items.filter((m) => m.sk !== sk),
    }
    notify(teamPk)
}

export const refetchTeamMembers = async (pk) => {
    if (!pk) return
    try {
        const result = await runQuery({ pk })
        storeMembers(pk, result)
        notify(pk)
    } catch (response) {
        console.error(response?.errors || response)
    }
}

export const refetchAllTeamMembers = (pks) =>
    Promise.all((pks || []).filter(Boolean).map((pk) => refetchTeamMembers(pk)))

// A team page loads the roster fresh through getTeam. Push that roster into
// this cache so the Teams landing pages (which keep their rosters from their
// own first load) show the same members and count when the user comes back,
// without waiting for a full refresh. getTeam members carry `position`
// instead of `positionId`; derive it so consumers keyed on positionId (the
// assignment popup, the member filter) keep working.
export const syncTeamMembersFromTeam = (teamPk, members) => {
    if (!teamPk || !Array.isArray(members)) return
    const items = members.map((m) => ({
        ...m,
        pk: m.pk || teamPk,
        positionId: m.positionId ?? (m.position?.sk ? `${m.position.sk}`.split('#').pop() : null),
    }))
    storeMembers(teamPk, { items })
    notify(teamPk)
}

// Team member records snapshot the occupant's name/phones at assignment; the
// backend re-syncs them when the occupant is edited. Refetch the cached teams
// on the edit event so open member lists pick the changes up without a refresh.
if (typeof window !== 'undefined') {
    window.addEventListener('occupant-data-updated', () => {
        Object.keys(cache).forEach((key) => refetchTeamMembers(key))
    })
}

function useListTeamMembers(pk, d1) {
    const [error, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [teamMembers, setTeamMembers] = useState([])

    const cacheKey = pk || d1

    useEffect(() => {
        let stale = false

        const handleTeamMembers = (_members) => {
            setTeamMembers(_members.items)
            setLoading(false)
        }

        async function fetch() {
            const cached = cache[cacheKey]
            if (cached) handleTeamMembers(cached)
            try {
                const result = await runQuery({ pk, d1 })
                if (stale) return
                storeMembers(cacheKey, result)
                handleTeamMembers(cache[cacheKey] || result)
            } catch (response) {
                console.error(response.errors)
                if (!stale) setErrors(response.errors)
            }
        }

        if (!cacheKey) {
            setLoading(false)
            return undefined
        }

        const unsubscribe = subscribe(cacheKey, (items) => {
            if (!stale) setTeamMembers(items)
        })

        setLoading(true)
        fetch()

        return () => {
            stale = true
            unsubscribe()
        }
    }, [pk, d1, cacheKey])

    return { error, loading, teamMembers }
}

// Members of ALL the given teams, merged. The backend resolver only supports
// querying by team pk (the d1 argument returns nothing), so this fans out one
// request per team and flattens the results. Each item keeps pk = its team's pk.
export function useListAllTeamMembers(teams) {
    // loading is derived at render time (pksKey !== loadedKey) rather than set
    // in the effect: the effect-based flag stayed false for the render right
    // after the team list arrived, and consumers acting on that stale "done"
    // (e.g. OccupantView clearing a dashboard deep-link because the membership
    // wasn't loaded yet) saw an empty member list they took as final.
    const [loadedKey, setLoadedKey] = useState('')
    const [allTeamMembers, setAllTeamMembers] = useState([])

    const pksKey = (teams || []).map((t) => t.pk).filter(Boolean).sort().join(',')
    const loading = pksKey !== loadedKey

    useEffect(() => {
        let stale = false
        const pks = pksKey ? pksKey.split(',') : []

        if (pks.length === 0) {
            setAllTeamMembers([])
            setLoadedKey('')
            return undefined
        }

        const rebuild = () => {
            if (stale) return
            setAllTeamMembers(pks.flatMap((pk) => cache[pk]?.items || []))
        }
        const unsubscribes = pks.map((pk) => subscribe(pk, rebuild))

        Promise.all(
            pks.map((pk) =>
                API.graphql(graphqlOperation(op, { pk, sk: 'TEAM_MEMBER' }))
                    .then((response) => {
                        // Store through the recent-changes merge so a stale
                        // (eventually-consistent) response can't drop a member
                        // added this session or resurrect a removed one.
                        storeMembers(pk, response.data.listTeamMembers)
                        return cache[pk]?.items || []
                    })
                    .catch((response) => {
                        console.error(response.errors || response)
                        return []
                    })
            )
        ).then((results) => {
            if (stale) return
            setAllTeamMembers(results.flat())
            setLoadedKey(pksKey)
        })

        return () => {
            stale = true
            unsubscribes.forEach((fn) => fn())
        }
    }, [pksKey])

    return { loading, allTeamMembers }
}

export default useListTeamMembers